// src/stores/models/useProfile.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/plugins/axios'
import alerts from '@/utils/services/alerts' // Assuming you have an alerts utility

export const useProfile = defineStore('profile', () => {
  // — state —
  const profileChecked   = ref(false)
  const id               = ref<number | null>(null)
  const username         = ref('')
  const email            = ref('')
  const profilePic       = ref('')
  const firstName        = ref('')
  const lastName         = ref('')
  const bio              = ref('')
  const birthday         = ref('')
  const isEmailConfirmed = ref(false)

  // stats
  const followerCount    = ref(0)
  const followingCount   = ref(0)
  const postCount        = ref(0)
  const commentCount     = ref(0)
  const ratingCount      = ref(0)
  const favoriteCount    = ref(0)

  // — getters —
  const fullName = computed(() => `${firstName.value} ${lastName.value}`.trim())

  // — actions —
  function setProfile(data: any) {
    id.value               = data.id ?? null
    username.value         = data.username ?? ''
    email.value            = data.email ?? ''
    profilePic.value       = data.profile_pic_url ?? ''
    firstName.value        = data.first_name ?? ''
    lastName.value         = data.last_name ?? ''
    bio.value              = data.bio ?? ''
    birthday.value         = data.birthday ?? ''
    // CORRECTED: Check `email_confirmed` field and convert 1/0 to true/false
    isEmailConfirmed.value = data.email_confirmed === 1

    followerCount.value    = data.follower_count ?? 0
    followingCount.value   = data.following_count ?? 0
    postCount.value        = data.post_count ?? 0
    commentCount.value     = data.comment_count ?? 0
    ratingCount.value      = data.rating_count ?? 0
    favoriteCount.value    = data.favorite_count ?? 0
  }

  // MODIFIED: Replaced setFollowingCount with a function to increment/decrement
  function updateFollowingCount(change: 1 | -1) {
    followingCount.value += change;
  }

  async function fetchProfile() {
    if (profileChecked.value) {
      return { success: true, user: { /* already set */ } }
    }
    try {
      const res = await api.get('/api/get-profile.php', { withCredentials: true })
      if (res.data.success) {
        setProfile(res.data.user)
        return { success: true, user: res.data.user }
      } else {
        resetProfile()
        return { success: false, error: res.data.error }
      }
    } catch {
      resetProfile()
      return { success: false, error: 'Network or server error' }
    } finally {
      profileChecked.value = true
    }
  }

  function resetProfile() {
    id.value               = null
    username.value         = ''
    email.value            = ''
    profilePic.value       = ''
    firstName.value        = ''
    lastName.value         = ''
    bio.value              = ''
    birthday.value         = ''
    isEmailConfirmed.value = false 
    followerCount.value    = 0
    followingCount.value   = 0
    postCount.value        = 0
    commentCount.value     = 0
    ratingCount.value      = 0
    favoriteCount.value    = 0
    profileChecked.value   = false
  }

  function updateProfilePicture(newUrl: string) {
    profilePic.value = newUrl
  }

  async function resendVerificationEmail() {
    try {
      const res = await api.post('/api/resend-verification-email.php');
      if (res.data.success) {
        alerts.toast.success('Verification email sent! Check your inbox.');
      } else {
        alerts.toast.error(res.data.error || 'Failed to send email.');
      }
    } catch (error) {
      alerts.toast.error('A network error occurred.');
    }
  }

  return {
    // state
    profileChecked,
    id,
    username,
    email,
    profilePic,
    firstName,
    lastName,
    bio,
    birthday,
    isEmailConfirmed, // CORRECTED
    followerCount,
    followingCount,
    postCount,
    commentCount,
    ratingCount,
    favoriteCount,

    // getters
    fullName,

    // actions
    setProfile,
    fetchProfile,
    resetProfile,
    updateProfilePicture,
    updateFollowingCount,
    resendVerificationEmail,
  }
})